import { ref, computed, watch } from '@vue/composition-api'
import i18n from '@/libs/i18n'

const STORAGE_KEY = 'assetListSelectedColumns'
const ORDER_STORAGE_KEY = 'assetListColumnOrder'
const COLUMNS_VERSION = 'v5'
const VERSION_KEY = 'assetListColumnsVersion'
const NEW_COLUMNS_V5 = ['hourmeter', 'odometer', 'chassis', 'lastPositionTime', 'lastTelemetryTime']
const REMOVED_COLUMNS_V5 = ['lastconnection']

export const DEFAULT_COLUMNS = [
  'ownername',
  'costCenter',
  'plate',
  'nickname',
  'modelName',
  'chassis',
  'hourmeter',
  'odometer',
  'lastPositionTime',
  'lastTelemetryTime',
  'workstatus',
  'actions',
]

export default function useAssetColumns() {
  // Master list of all table column definitions
  const allColumns = computed(() => [
    { key: 'ownername', label: i18n.t('Owner'), sortable: true, class: 'col-ownername' },
    { key: 'costCenter', label: i18n.t('Cost Center'), sortable: false, class: 'col-costCenter' },
    { key: 'plate', label: i18n.t('Plate'), sortable: true, class: 'col-plate' },
    { key: 'nickname', label: i18n.t('Nickname'), sortable: true, class: 'col-nickname' },
    { key: 'modelName', label: i18n.t('Model'), sortable: false, class: 'col-modelName' },
    { key: 'chassis', label: i18n.t('Chassis'), sortable: true, class: 'col-chassis' },
    { key: 'hourmeter', label: i18n.t('Hourmeter'), sortable: false, class: 'col-hourmeter' },
    { key: 'odometer', label: i18n.t('Odometer'), sortable: false, class: 'col-odometer' },
    { key: 'lastPositionTime', label: i18n.t('Last Position'), sortable: true, class: 'col-lastPositionTime' },
    { key: 'lastTelemetryTime', label: i18n.t('Last Telemetry'), sortable: true, class: 'col-lastTelemetryTime' },
    { key: 'workstatus', label: i18n.t('Status'), sortable: true, class: 'col-workstatus' },
    { key: 'actions', label: i18n.t('Actions'), sortable: false, class: 'col-actions' },
  ])

  // Formatted list for the selection dropdown UI
  const availableColumns = computed(() => allColumns.value.map(col => ({
    key: col.key,
    label: col.label || col.key,
  })))

  // ====================================================
  // COLUMN ORDER — persistência da ordenação drag & drop
  // ====================================================
  const loadSavedOrder = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const savedVersion = localStorage.getItem(VERSION_KEY)
        const stored = localStorage.getItem(ORDER_STORAGE_KEY)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (Array.isArray(parsed) && parsed.length > 0) {
            const validKeys = DEFAULT_COLUMNS
            const filtered = parsed.filter(key => validKeys.includes(key) && !REMOVED_COLUMNS_V5.includes(key))
            if (filtered.length > 0) {
              if (savedVersion !== COLUMNS_VERSION) {
                NEW_COLUMNS_V5.forEach(key => {
                  if (!filtered.includes(key)) {
                    const actIdx = filtered.indexOf('actions')
                    if (actIdx !== -1) filtered.splice(actIdx, 0, key)
                    else filtered.push(key)
                  }
                })
                localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(filtered))
                localStorage.setItem(VERSION_KEY, COLUMNS_VERSION)
              }
              return filtered
            }
          }
        }
      }
    } catch (e) {
      // Storage unavailable or corrupted
    }
    return [...DEFAULT_COLUMNS]
  }

  const columnOrder = ref(loadSavedOrder())

  const saveOrderToStorage = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(columnOrder.value))
        localStorage.setItem(VERSION_KEY, COLUMNS_VERSION)
      }
    } catch (e) {
      // Storage quota or permission error
    }
  }

  watch(columnOrder, () => { saveOrderToStorage() }, { deep: true })

  /**
   * Move uma coluna de fromIndex para toIndex na ordem.
   * Usado pelo drag & drop dos headers da tabela.
   */
  const moveColumn = (fromIndex, toIndex) => {
    const order = [...columnOrder.value]
    const [moved] = order.splice(fromIndex, 1)
    order.splice(toIndex, 0, moved)
    columnOrder.value = order
  }

  /**
   * Reordena pelo key: move a coluna `key` da posição atual para `toIndex`.
   */
  const moveColumnByKey = (key, toIndex) => {
    const order = [...columnOrder.value]
    const fromIndex = order.indexOf(key)
    if (fromIndex === -1) return
    const [moved] = order.splice(fromIndex, 1)
    order.splice(toIndex, 0, moved)
    columnOrder.value = order
  }

  // ====================================================
  // COLUMN SELECTION — quais colunas estão visíveis
  // ====================================================
  const loadSavedColumns = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const savedVersion = localStorage.getItem(VERSION_KEY)
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (Array.isArray(parsed) && parsed.length > 0) {
            const validKeys = DEFAULT_COLUMNS
            const filtered = parsed.filter(key => validKeys.includes(key) && !REMOVED_COLUMNS_V5.includes(key))
            if (filtered.length > 0) {
              if (savedVersion !== COLUMNS_VERSION) {
                NEW_COLUMNS_V5.forEach(key => {
                  if (!filtered.includes(key)) {
                    const actIdx = filtered.indexOf('actions')
                    if (actIdx !== -1) filtered.splice(actIdx, 0, key)
                    else filtered.push(key)
                  }
                })
                localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))
                localStorage.setItem(VERSION_KEY, COLUMNS_VERSION)
              }
              return filtered
            }
          }
        }
      }
    } catch (e) {
      // Storage unavailable or corrupted
    }
    return [...DEFAULT_COLUMNS]
  }

  const selectedColumns = ref(loadSavedColumns())

  const saveColumnsToStorage = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedColumns.value))
        localStorage.setItem(VERSION_KEY, COLUMNS_VERSION)
      }
    } catch (e) {
      // Storage quota or permission error
    }
  }

  watch(selectedColumns, () => { saveColumnsToStorage() }, { deep: true })

  // Active fields for b-table — respeita a ordem de columnOrder
  const tableColumns = computed(() => {
    if (!selectedColumns.value || selectedColumns.value.length === 0) {
      return allColumns.value.filter(col => col.key === 'plate')
    }

    // Montar mapa de definição de colunas por key
    const colMap = {}
    allColumns.value.forEach(col => { colMap[col.key] = col })

    // Usar columnOrder para determinar a sequência, filtrando apenas as selecionadas
    const ordered = []
    columnOrder.value.forEach(key => {
      if (selectedColumns.value.includes(key) && colMap[key]) {
        ordered.push(colMap[key])
      }
    })

    // Adicionar colunas selecionadas que podem não estar no columnOrder (segurança)
    selectedColumns.value.forEach(key => {
      if (!ordered.find(c => c.key === key) && colMap[key]) {
        ordered.push(colMap[key])
      }
    })

    return ordered.length > 0 ? ordered : allColumns.value.filter(col => col.key === 'plate')
  })

  // Colunas disponíveis ordenadas pelo columnOrder (para o dropdown de seleção)
  const orderedAvailableColumns = computed(() => {
    const colMap = {}
    availableColumns.value.forEach(col => { colMap[col.key] = col })

    const ordered = []
    columnOrder.value.forEach(key => {
      if (colMap[key]) {
        ordered.push(colMap[key])
      }
    })

    // Adicionar colunas que podem não estar no order
    availableColumns.value.forEach(col => {
      if (!ordered.find(c => c.key === col.key)) {
        ordered.push(col)
      }
    })

    return ordered
  })

  const selectAllColumns = () => {
    selectedColumns.value = [...DEFAULT_COLUMNS]
  }

  const deselectAllColumns = () => {
    selectedColumns.value = ['plate']
  }

  const resetColumnsToDefault = () => {
    selectedColumns.value = [...DEFAULT_COLUMNS]
    columnOrder.value = [...DEFAULT_COLUMNS]
  }

  return {
    allColumns,
    availableColumns,
    orderedAvailableColumns,
    selectedColumns,
    tableColumns,
    columnOrder,
    selectAllColumns,
    deselectAllColumns,
    resetColumnsToDefault,
    moveColumn,
    moveColumnByKey,
  }
}
